import { useState, useEffect } from "react";
import NoScrollLink from "../components/NoScrollLink";
import Layout from "../components/Layout";
import { Button } from "../components/FormElements";
import { createCheckout, checkoutInventorySync } from "../lib/shopify";
import { useCart } from "react-use-cart";
import Image from "next/image";
import Head from "next/head";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { priceDecimal, priceRound } from "../lib/formatPrice";

export default function CartPage() {
  const { isEmpty, items, cartTotal, removeItem, updateItemQuantity } =
    useCart();
  const [isReady, setIsReady] = useState(false);
  const [alertNotice, setAlertNotice] = useState(false);

  const total = cartTotal;

  useEffect(() => {
    setIsReady(true);
  }, []);

  async function checkout() {
    const lineItems = items.map((item) => {
      return {
        variantId: item.id,
        quantity: item.quantity,
      };
    });

    const data = await createCheckout(lineItems);
    window.location.href = data.webUrl;
  }

  async function inventorySync() {
    if (items.length === 0) return;

    // Prepare the query to send to Shopify
    const itemTitles = items.map((item) => {
      return `(title:${item.title})`;
    });
    let queryString = itemTitles.join(" OR ");

    // Send query to Shopify
    const inventoryItems = await checkoutInventorySync(queryString);

    //console.log("inventoryItems: ", inventoryItems);

    // Loop through returned results and calculate the remaining inventory
    // TODO also remove anything that didn't match Shopify
    const matchedInventory = inventoryItems.map((inventoryItem) => {
      let madeToOrderQty = 0;

      inventoryItem.node.variants.edges.map((variant) => {
        if (variant.node.title === "Made to Order") {
          madeToOrderQty = variant.node.quantityAvailable;
        }
      });

      let readymadeQty = inventoryItem.node.totalInventory - madeToOrderQty;

      return {
        title: inventoryItem.node.title,
        quantity: readymadeQty,
      };
    });

    //console.log("Matched inventory: ", matchedInventory);

    // Go through all the items in the cart,
    // compare them to the items in the returned inventory.
    items.map((item) => {
      //console.log(item);

      let found = false;

      matchedInventory.map((matchedItem) => {
        if (item.title === matchedItem.title) {
          //console.log(matchedItem);

          found = true;

          if (item.quantity > matchedItem.quantity) {
            updateItemQuantity(item.id, matchedItem.quantity);
            setAlertNotice("We had to update your cart. Soz.");
          }
        }
      });

      if (!found) {
        removeItem(item.id);
        setAlertNotice("We had to update your cart. Soz.");
      }
    });
  }

  // This ONLY runs on load
  useEffect(() => {
    inventorySync();
  }, []);

  // Run this when this component on page load and rerender
  useEffect(() => {
    const timer = setInterval(inventorySync, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  });

  useEffect(() => {
    if (alertNotice) {
      const timer = setTimeout(() => {
        setAlertNotice(false);
      }, 15 * 1000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [alertNotice]);

  if (!isReady) {
    return null;
  }

  if (isEmpty) {
    return (
      <Layout>
        <Head>
          <title>veikkanen - Cart</title>
        </Head>
        <EmptyCart>
          <CartTitle>Cart is Empty.</CartTitle>
          <AnimatePresence>
            {alertNotice && (
              <AlertNotice
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "spring", duration: 1 }}
              >
                One or more of your items is no longer&nbsp;available.
                <br />
                Your cart has been updated.
              </AlertNotice>
            )}
          </AnimatePresence>
        </EmptyCart>
      </Layout>
    );
  }

  const cartItems = items.map((item, i) => {
    let unformattedPrice = item.price;

    let formattedPrice =
      unformattedPrice % 1
        ? priceDecimal.format(unformattedPrice)
        : priceRound.format(unformattedPrice);

    return (
      <ItemRow key={`item-${i}`}>
        <ItemRowGrid>
          <ItemRowInner>
            <NoScrollLink href={`/work/${item.handle}`} passHref>
              <CartImageWrap>
                <CartImage
                  src={item.image.transformedSrc}
                  width="208"
                  height="208"
                />
              </CartImageWrap>
            </NoScrollLink>
            <NoScrollLink href={`/work/${item.handle}`} passHref>
              <CartItemTitle>{item.title}</CartItemTitle>
            </NoScrollLink>
          </ItemRowInner>
          <ItemRowInner>
            <CartItemPrice>
              {item.quantity} &times; {formattedPrice}
            </CartItemPrice>

            <CartRemoveItem onClick={() => removeItem(item.id)}>
              &times;
            </CartRemoveItem>
          </ItemRowInner>
        </ItemRowGrid>
      </ItemRow>
    );
  });

  let unformattedTotal = total;

  let formattedTotal =
    unformattedTotal % 1
      ? priceDecimal.format(unformattedTotal)
      : priceRound.format(unformattedTotal);

  return (
    <Layout>
      <Head>
        <title>veikkanen - Cart</title>
      </Head>
      <CartWrapper>
        <CartTitle>Your Items.</CartTitle>
        <AnimatePresence>
          {alertNotice && (
            <AlertNotice
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", duration: 1 }}
            >
              One or more of your items is no longer&nbsp;available.
              <br />
              Your cart has been updated.
            </AlertNotice>
          )}
        </AnimatePresence>
        {cartItems}
        <ItemRow key="last">
          <TotalWrapper>
            <TotalPrice>Cart total: {formattedTotal}</TotalPrice>
            <Button onClick={checkout}>Proceed to Checkout</Button>
            {/* <Button onClick={inventorySync}>Sync Inventory</Button> */}
          </TotalWrapper>
        </ItemRow>
      </CartWrapper>
    </Layout>
  );
}

const CartWrapper = styled.div`
  padding: var(--gridMarginGap);
  padding-top: 20vh;

  min-height: calc(100vh - var(--gridMarginGap) * 6);

  @media screen and (min-width: 768px) {
    min-height: calc(100vh - var(--gridMarginGap) * 2);
  }
`;

const EmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: calc(100vh - var(--gridMarginGap) * 6);

  @media screen and (min-width: 768px) {
    min-height: calc(100vh - var(--gridMarginGap) * 2);
  }
`;

const CartTitle = styled.div`
  font-family: var(--brandFont);
  color: var(--colorContentPrimary);
  margin: calc(var(--gridMarginGap) * 2);

  font-size: var(--step-3-5);
  font-weight: 300;

  text-align: center;
`;

const ItemRow = styled.div`
  width: 100%;
  display: grid;
  margin-bottom: var(--gridMarginGap);

  grid-template-columns: var(--gridTemplateColumnsDefault);
  column-gap: var(--gridColumnGap);
  grid-template-areas: "a a a a a a";

  @media screen and (min-width: 768px) {
    grid-template-areas: ". . a a a a a a a a . .";
  }
`;

const ItemRowGrid = styled.div`
  display: flex;
  grid-area: a;
  align-items: center;
  justify-content: space-between;

  @media screen and (min-width: 768px) {
  }
`;

const ItemRowInner = styled.div`
  display: flex;
  align-items: center;
`;

const CartImage = styled(Image)`
  &:hover {
    cursor: pointer;
  }
`;

const CartImageWrap = styled.div`
  max-width: calc(var(--gridMarginGap) * 6);
  @media screen and (min-width: 768px) {
    max-width: calc(var(--gridMarginGap) * 8);
  }
`;

const CartItemTitle = styled.h2`
  font-size: var(--step-2);
  font-weight: 400;
  color: var(--colorUIPrimary);
  grid-area: title;

  margin-left: var(--gridMarginGap);

  &:hover {
    cursor: pointer;
    color: var(--colorUISecondary);
  }
`;

const CartItemPrice = styled.span`
  font-size: var(--step-2);
  text-align: right;
  font-weight: 300;
  color: var(--colorContentPrimary);
  grid-area: price;

  @media screen and (min-width: 768px) {
    font-size: var(--step-2);
  }
`;

const TotalPrice = styled.div`
  font-size: var(--step-2);
  text-align: center;
  color: var(--colorContentPrimary);
`;

const CartRemoveItem = styled.button`
  font-size: var(--step-2-5);
  font-weight: 400;
  color: var(--colorContentPrimary);
  grid-area: buttons;
  text-align: center;
  width: var(--step-2-5);
  height: var(--step-2-5);
  line-height: 0.85em;
  margin-left: var(--gridMarginGap);

  justify-self: end;

  @media screen and (min-width: 768px) {
    font-size: var(--step-2);
  }

  &:hover {
    cursor: pointer;
    color: var(--colorUISecondary);
  }
`;

const TextButton = styled.button`
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  outline: none;
  text-align: inherit;
  color: inherit;
  font: inherit;
  text-decoration: underline;

  line-height: normal;

  -webkit-appearance: none;
  color: var(--colorUIPrimary);
  font-family: var(--brandFont);
  font-weight: 300;

  opacity: ${(props) => (!props.disabled ? 1 : 0.3)};

  padding: calc(var(--step-1) / 2) var(--step-1);
  margin: var(--step-2) 0;

  font-size: var(--step-1-75);

  &:hover {
    cursor: ${(props) => (!props.disabled ? `pointer` : `not-allowed`)};
    background-color: ${(props) =>
      !props.disabled && `var(--colorUISecondary)`};
  }
`;

const TotalWrapper = styled.div`
  border-top: 1px solid var(--colorUIPrimary);
  padding-top: calc(var(--gridMarginGap));
  grid-area: a;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const AlertNotice = styled(motion.div)`
  text-align: center;
  font-weight: 400;
  color: var(--colorBrandPink);
  margin-bottom: var(--gridMarginGap);
`;
